/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * State shared by both thumbnail strategies for the lifetime of one piece of
 * content: the session token, the last-known-good frame, and the memory
 * diagnostics counters.
 *
 * These three are shared for one reason each, and none is incidental:
 *
 *   - The GENERATION TOKEN is the only thing that can stop a late fetch
 *     continuation writing a blob URL nobody can revoke, so both strategies
 *     must consult the same one — a per-strategy token would let a reset
 *     invalidate one path while the other kept minting orphans.
 *   - The LAST-KNOWN-GOOD frame is what either lookup returns on a miss, and
 *     it must be cleared as one on teardown or a frame from the previous asset
 *     bleeds into new content.
 *   - The DIAGNOSTICS counters are cumulative across the whole manager:
 *     `blobsCreated − blobsRevoked` is the leak indicator, and it is only
 *     meaningful if BOTH paths report into the same pair. The browser path
 *     creates and revokes object URLs too.
 */

/** Cumulative memory counters for one manager instance. */
export interface TThumbnailDiagnosticCounters {
  fetchesIssued: number;
  blobsCreated: number;
  blobsRevoked: number;
  evictions: number;
  thumbnailsDelivered: number;
  peakCacheSize: number;
  peakActiveRequests: number;
}

/**
 * Clear a timer handle and yield the value to store back into the field.
 *
 * Written as `this.mTimer = clearTimer(this.mTimer)` because TypeScript cannot
 * null a caller's field through a parameter — returning null is the only shape
 * that keeps the null-check and the clear inseparable. Every timer these
 * classes own has to be cleared in three or four teardown paths, and one missed
 * site is a leak, so the boilerplate is worth collapsing.
 */
export function clearTimer(handle: ReturnType<typeof setTimeout> | null): null {
  if (handle !== null) {
    clearTimeout(handle);
  }
  return null;
}

export class ThumbnailSession {
  /**
   * Monotonic session token, incremented whenever fetch state is torn down
   * (resetThumbnails, setFetchSuspended(true)).
   *
   * Every async fetch continuation captures the value in effect when it was
   * issued; a mismatch means the continuation belongs to a superseded session
   * and must return before touching any state.
   *
   * This exists because AbortController.abort() does NOT cancel the blob
   * conversion of a response that has already resolved. Without the token, a
   * late `.blob()` callback still runs URL.createObjectURL and writes a cache
   * entry — either onto a manager instance nobody holds any more (so the blob
   * URL can never be revoked and leaks for the document's lifetime), or onto
   * the live instance using an offset from the previous programme, where it
   * cannot be evicted and can be pinned permanently once displayed.
   */
  private mGeneration: number = 0;

  /**
   * The most recently delivered real (non-fallback) thumbnail blob URL.
   * Returned as the fallback whenever a lookup misses, so the UI keeps showing
   * a genuine previously-seen frame instead of an unfetched raw CDN URL (which
   * would render blank/broken in the <img> until it loads). Only null before
   * the very first frame has ever loaded for the current content.
   */
  private mLastDelivered: string | null = null;

  /**
   * Listeners notified whenever a thumbnail blob lands in either strategy.
   *
   * A lookup that misses returns mLastDelivered and enqueues the real fetch, so
   * the value the View holds is only correct until that fetch completes. The
   * View has no other way to learn that it did: nothing polls the cache, and a
   * memoised or otherwise cached lookup would keep showing the stale frame
   * indefinitely (which is exactly what happened while a trick-play speed
   * change left the seek position unmoved). One callback per arrival is far
   * cheaper than re-running the lookup on every render.
   */
  private readonly mListeners: Set<() => void> = new Set();

  private mFetchesIssued: number = 0;
  private mBlobsCreated: number = 0;
  private mBlobsRevoked: number = 0;
  private mEvictions: number = 0;
  private mThumbnailsDelivered: number = 0;
  private mPeakCacheSize: number = 0;
  private mPeakActiveRequests: number = 0;

  /**
   * Register a listener fired whenever a thumbnail blob becomes available.
   *
   * Deliberately NOT cleared by invalidate() or by the manager's reset: the
   * subscriber owns its own lifetime (a View unmount calls the returned
   * unsubscribe), and dropping listeners on a content transition would leave
   * the next asset's arrivals unobserved.
   *
   * @param listener - Called with no arguments after each successful fetch.
   * @returns An unsubscribe function; safe to call more than once.
   */
  public subscribe(listener: () => void): () => void {
    this.mListeners.add(listener);
    return (): void => {
      this.mListeners.delete(listener);
    };
  }

  /**
   * Notify every listener that a new thumbnail is available.
   *
   * Each listener is isolated: one throwing subscriber must not abort the
   * notification loop, and must certainly not propagate back into the fetch
   * continuation that called this — that would leave the concurrency slot
   * accounting half-applied.
   */
  public notifyThumbnailAvailable(): void {
    this.mListeners.forEach((listener: () => void): void => {
      try {
        listener();
      } catch {
        // Intentionally swallowed — see the note above.
      }
    });
  }

  /** Invalidate every in-flight continuation. Call BEFORE aborting or revoking. */
  public invalidate(): void {
    this.mGeneration++;
  }

  /** The token a fetch should capture at issue time. */
  public currentGeneration(): number {
    return this.mGeneration;
  }

  public getLastDelivered(): string | null {
    return this.mLastDelivered;
  }

  public setLastDelivered(blobUrl: string | null): void {
    this.mLastDelivered = blobUrl;
  }

  /**
   * Record delivery of a frame, counting it only when it differs from the one
   * already on screen.
   *
   * Deliberately NOT counted on neighbour-probe hits — `thumbnailsDelivered` is
   * the denominator of the wasted-work ratio against `fetchesIssued`, and
   * counting a fallback frame as a delivery would flatter it.
   */
  public recordExactDelivery(blobUrl: string): void {
    if (blobUrl !== this.mLastDelivered) {
      this.mThumbnailsDelivered++;
    }
    this.mLastDelivered = blobUrl;
  }

  public recordFetchIssued(): void {
    this.mFetchesIssued++;
  }

  public recordBlobRevoked(): void {
    this.mBlobsRevoked++;
  }

  public recordEviction(): void {
    this.mEvictions++;
  }

  public recordCacheSize(size: number): void {
    if (size > this.mPeakCacheSize) {
      this.mPeakCacheSize = size;
    }
  }

  public recordActiveRequests(count: number): void {
    if (count > this.mPeakActiveRequests) {
      this.mPeakActiveRequests = count;
    }
  }

  /**
   * Clear the per-content peaks. blobsCreated and blobsRevoked stay cumulative
   * so `created − revoked` remains a true running total across content
   * transitions — it is the leak indicator and should read ~0 after a reset.
   */
  public resetPeaks(): void {
    this.mPeakCacheSize = 0;
    this.mPeakActiveRequests = 0;
  }

  public getCounters(): TThumbnailDiagnosticCounters {
    return {
      fetchesIssued: this.mFetchesIssued,
      blobsCreated: this.mBlobsCreated,
      blobsRevoked: this.mBlobsRevoked,
      evictions: this.mEvictions,
      thumbnailsDelivered: this.mThumbnailsDelivered,
      peakCacheSize: this.mPeakCacheSize,
      peakActiveRequests: this.mPeakActiveRequests
    };
  }

  /**
   * Run the guarded fetch → blob → object-URL pipeline shared by both
   * strategies, and hand the resulting blob URL to `onBlob`.
   *
   * The generation token is captured at issue time and re-checked at EVERY
   * await boundary. The check inside `.blob()` is the load-bearing one and it
   * must precede URL.createObjectURL — which is exactly why that call lives
   * here rather than in the callers. Returning early leaves the Blob
   * unreferenced for the GC; creating the URL first would pin native memory
   * behind a handle the superseded session can never revoke. Centralising it
   * means neither strategy can regress that invariant independently.
   *
   * Callers keep their own bookkeeping: the browser path owns entry status and
   * queue advancement, the 10-foot path owns the cache, concurrency slots and
   * eviction. Only the pipeline is shared.
   *
   * @param url     - Absolute URL to fetch.
   * @param signal  - Abort signal for this request.
   * @param onBlob  - Receives the freshly created object URL on success.
   * @param onError - Receives an HTTP status description or a thrown error.
   * @param onAbort - Runs when the request was aborted (never also onError).
   */
  public fetchGuarded(
    url: string,
    signal: AbortSignal,
    onBlob: (blobUrl: string) => void,
    onError: (reason: unknown) => void,
    onAbort: () => void
  ): void {
    const generation: number = this.mGeneration;
    const isCurrent = (): boolean => generation === this.mGeneration;

    fetch(url, { signal: signal })
      .then((response: Response) => {
        if (!isCurrent()) {
          return;
        }
        if (!response.ok) {
          onError("HTTP " + String(response.status));
          return;
        }
        response
          .blob()
          .then((blob: Blob) => {
            // MUST precede createObjectURL — see the note above.
            if (!isCurrent()) {
              return;
            }
            const blobUrl = URL.createObjectURL(blob);
            this.mBlobsCreated++;
            onBlob(blobUrl);
          })
          .catch((error: unknown) => {
            if (!isCurrent()) {
              return;
            }
            onError(error);
          });
      })
      .catch((error: unknown) => {
        if (!isCurrent()) {
          return;
        }
        if (error instanceof DOMException && error.name === "AbortError") {
          onAbort();
          return;
        }
        onError(error);
      });
  }
}
